
import { useState } from 'react';
import Button from '../../../../components/base/Button';
import { useNavigate } from 'react-router-dom';

export default function PricingSection() {
  const [selectedPlan, setSelectedPlan] = useState<string | null>(null);
  const navigate = useNavigate();

  const handleNavigation = (path: string) => {
    navigate(path);
    window.scrollTo(0, 0);
  };

  const scrollToSection = (sectionId: string) => {
    const element = document.getElementById(sectionId);
    if (element) {
      element.scrollIntoView({ behavior: 'smooth' });
    }
  };

  const plans = [
    {
      name: 'Inicial',
      price: '$49',
      period: '/mes',
      description: 'Perfecto para transacciones de bajo volumen',
      features: [
        'Hasta 10 transacciones/mes',
        'Contabilidad básica',
        'Estado de P&G simple',
        'Soporte por email',
        'Integración MS Business Central'
      ],
      popular: false,
      buttonAction: () => handleNavigation('/es/starter-plan')
    },
    {
      name: 'Profesional',
      price: '$99',
      period: '/mes',
      description: 'Ideal para empresas simples de propietario-operador',
      features: [
        'Hasta 150 transacciones/mes',
        'Contabilidad completa',
        'Estados financieros mensuales',
        'Nómina básica (hasta 2 empleados)',
        'Procesamiento AP/AR',
        'Soporte preparación fiscal trimestral',
        'Soporte telefónico y email',
        'Integración multiplataforma'
      ],
      popular: false,
      buttonAction: () => handleNavigation('/es/professional-plan')
    },
    {
      name: 'Empresarial',
      price: '$249',
      period: '/mes',
      description: 'Para operadores complejos con activos',
      features: [
        'Hasta 300 transacciones/mes',
        'Contabilidad avanzada',
        'Gestión de activos y depreciación',
        'Seguimiento de inventario',
        'Contabilidad de costos',
        'Cierres mensuales y trimestrales',
        'Reportes financieros detallados',
        'Asistencia en planificación fiscal',
        'Soporte prioritario',
        'Todas las integraciones de plataforma'
      ],
      popular: true,
      buttonAction: () => handleNavigation('/es/business-plan')
    },
    {
      name: 'Crecimiento',
      price: '$499',
      period: '/mes',
      description: 'Empresas con hasta 10 empleados',
      features: [
        'Todo en EMPRESARIAL más',
        'Hasta 250 transacciones/mes',
        'Contabilidad de servicio completo',
        'Contabilidad de Nómina (hasta 10 empleados)',
        'Contabilidad de costos',
        'Reportes financieros integrales',
        'Gerente de cuenta dedicado',
        'Reportes personalizados',
        'Reconciliación multi-banco (hasta 4 cuentas)',
        'Reportes de rendimiento de proveedores',
        'Llamada estratégica mensual con gerente financiero',
        'Verificación trimestral de riesgo y fraude',
        'Gestión de activos y depreciación'
      ],
      popular: false,
      buttonAction: () => scrollToSection('signup')
    },
    {
      name: 'Empresarial',
      price: '$999',
      period: '/mes',
      description: ' ',
      features: [
        'Todo en CRECIMIENTO más',
        'Hasta 350 transacciones/mes incluidas',
        'Suite de contabilidad avanzada',
        'Contabilidad de Nómina',
        'Contabilidad de Costos Avanzada y seguimiento de inventario',
        'Asistencia en preparación de auditorías',
        'Incorporación de guante blanco',
        'Soporte prioritario 24/7',
        'Integraciones personalizadas',
        'Soporte de cumplimiento y planificación fiscal',
        'Soporte de Cierre Anual',
        'Análisis y reportes avanzados',
        'Soporte multi-ubicación',
        'Flujos de trabajo personalizados',
        'Reportes consolidados multi-entidad y multi-moneda',
        'Sesiones de asesoría nivel CFO'
      ],
      popular: false,
      buttonAction: () => scrollToSection('signup')
    }
  ];

  return (
    <section id="pricing" className="py-20 bg-white">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        <div className="text-center mb-16">
          <h2 className="text-3xl md:text-4xl font-bold text-gray-900 mb-4">
            Precios Simples y Transparentes
          </h2>
          <p className="text-xl text-gray-600 max-w-3xl mx-auto">
            Elija el plan que se adapte a las necesidades de su negocio. Sin tarifas ocultas, sin sorpresas.
          </p>
        </div>

        {/* Pricing Cards */}
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 xl:grid-cols-5 gap-6">
          {plans.map((plan, index) => (
            plan.name === 'Profesional' ? (
              // Custom Professional Plan Card
              <div
                key={index}
                className="bg-white rounded-xl shadow-lg border-2 border-gray-200 relative"
              >
                <div className="p-6">
                  <h3 className="text-xl font-bold text-gray-900 mb-2">Profesional</h3>
                  <div className="mb-4">
                    <span className="text-3xl font-bold text-gray-900">$99</span>
                    <span className="text-gray-600">/mes</span>
                  </div>
                  <p className="text-gray-600 mb-6 text-sm">
                    Ideal para empresas simples de propietario-operador
                  </p>
                  <ul className="space-y-3 mb-6">
                    <li className="flex items-start text-sm">
                      <i className="ri-check-line text-green-500 mr-2 mt-0.5 flex-shrink-0"></i>
                      <span className="text-gray-700">Todo en INICIAL más</span>
                    </li>
                    <li className="flex items-start text-sm">
                      <i className="ri-check-line text-green-500 mr-2 mt-0.5 flex-shrink-0"></i>
                      <span className="text-gray-700">Hasta 50 transacciones/mes</span>
                    </li>
                    <li className="flex items-start text-sm">
                      <i className="ri-check-line text-green-500 mr-2 mt-0.5 flex-shrink-0"></i>
                      <span className="text-gray-700">Contabilidad básica</span>
                    </li>
                    <li className="flex items-start text-sm">
                      <i className="ri-check-line text-green-500 mr-2 mt-0.5 flex-shrink-0"></i>
                      <span className="text-gray-700">
                        Configuración de recordatorios automatizados
                      </span>
                    </li>
                    <li className="flex items-start text-sm">
                      <i className="ri-check-line text-green-500 mr-2 mt-0.5 flex-shrink-0"></i>
                      <span className="text-gray-700">Reconciliación de cuenta bancaria (1 cuenta)</span>
                    </li>
                    <li className="flex items-start text-sm">
                      <i className="ri-check-line text-green-500 mr-2 mt-0.5 flex-shrink-0"></i>
                      <span className="text-gray-700">Reconciliación de tarjeta de crédito (1 tarjeta)</span>
                    </li>
                  </ul>
                  <Button
                    variant="outline"
                    onClick={() => handleNavigation('/es/professional-plan')}
                    className="w-full"
                  >
                    Comenzar
                  </Button>
                </div>
              </div>
            ) : plan.name === 'Empresarial' && plan.price === '$249' ? (
              <div
                key={index}
                className="bg-white rounded-xl shadow-lg border-2 border-blue-500 relative transform scale-105"
              >
                <div className="absolute -top-4 left-1/2 transform -translate-x-1/2">
                  <span className="bg-blue-500 text-white px-4 py-1 rounded-full text-sm font-medium whitespace-nowrap">
                    Más Popular
                  </span>
                </div>
                <div className="p-6">
                  <h3 className="text-xl font-bold text-gray-900 mb-2">Empresarial</h3>
                  <div className="mb-4">
                    <span className="text-3xl font-bold text-gray-900">$249</span>
                    <span className="text-gray-600">/mes</span>
                  </div>
                  <p className="text-gray-600 mb-6 text-sm">Para operadores complejos con activos</p>
                  <ul className="space-y-3 mb-6">
                    <li className="flex items-start text-sm">
                      <i className="ri-check-line text-green-500 mr-2 mt-0.5 flex-shrink-0"></i>
                      <span className="text-gray-770">Todo en PROFESIONAL más</span>
                    </li>
                    <li className="flex items-start text-sm">
                      <i className="ri-check-line text-green-500 mr-2 mt-0.5 flex-shrink-0"></i>
                      <span className="text-gray-770">Hasta 150 transacciones/mes</span>
                    </li>
                    <li className="flex items-start text-sm">
                      <i className="ri-check-line text-green-500 mr-2 mt-0.5 flex-shrink-0"></i>
                      <span className="text-gray-770">Contabilidad estándar</span>
                    </li>
                    <li className="flex items-start text-sm">
                      <i className="ri-check-line text-green-500 mr-2 mt-0.5 flex-shrink-0"></i>
                      <span className="text-gray-770">Procesamiento AP/AR</span>
                    </li>
                    <li className="flex items-start text-sm">
                      <i className="ri-check-line text-green-500 mr-2 mt-0.5 flex-shrink-0"></i>
                      <span className="text-gray-770">Contabilidad de Nómina (hasta 5 empleados)</span>
                    </li>
                    <li className="flex items-start text-sm">
                      <i className="ri-check-line text-green-500 mr-2 mt-0.5 flex-shrink-0"></i>
                      <span className="text-gray-770">Soporte de cierre mensual</span>
                    </li>
                    <li className="flex items-start text-sm">
                      <i className="ri-check-line text-green-500 mr-2 mt-0.5 flex-shrink-0"></i>
                      <span className="text-gray-770">Reportes financieros detallados</span>
                    </li>
                    <li className="flex items-start text-sm">
                      <i className="ri-check-line text-green-500 mr-2 mt-0.5 flex-shrink-0"></i>
                      <span className="text-gray-770">Soporte preparación fiscal</span>
                    </li>
                    <li className="flex items-start text-sm">
                      <i className="ri-check-line text-green-500 mr-2 mt-0.5 flex-shrink-0"></i>
                      <span className="text-gray-770">Reconciliación cuenta bancaria (hasta 2)</span>
                    </li>
                    <li className="flex items-start text-sm">
                      <i className="ri-check-line text-green-500 mr-2 mt-0.5 flex-shrink-0"></i>
                      <span className="text-gray-770">Seguimiento contratos proveedores</span>
                    </li>
                    <li className="flex items-start text-sm">
                      <i className="ri-check-line text-green-500 mr-2 mt-0.5 flex-shrink-0"></i>
                      <span className="text-gray-770">Reconciliación tarjeta crédito (hasta 2)</span>
                    </li>
                    <li className="flex items-start text-sm">
                      <i className="ri-check-line text-green-500 mr-2 mt-0.5 flex-shrink-0"></i>
                      <span className="text-gray-770">Soporte telefónico</span>
                    </li>
                    <li className="flex items-start text-sm">
                      <i className="ri-check-line text-green-500 mr-2 mt-0.5 flex-shrink-0"></i>
                      <span className="text-gray-770">Todas las integraciones de plataforma</span>
                    </li>
                  </ul>
                  <Button
                    className="w-full bg-blue-600 hover:bg-blue-700 text-white shadow-lg hover:shadow-xl transition-all duration-300"
                    onClick={() => handleNavigation('/es/business-plan')}
                  >
                    Comenzar
                  </Button>
                </div>
              </div>
            ) : (
              // Generic Card for other plans
              <div
                key={index}
                className="relative bg-white rounded-xl shadow-lg hover:shadow-xl transition-all duration-300 border-2 border-gray-200"
              >
                <div className="p-6">
                  <h3 className="text-xl font-bold text-gray-900 mb-2">{plan.name}</h3>
                  <div className="mb-4">
                    <span className="text-3xl font-bold text-gray-900">{plan.price}</span>
                    <span className="text-gray-600">{plan.period}</span>
                  </div>
                  <p className="text-gray-600 mb-6 text-sm">{plan.description}</p>

                  <ul className="space-y-3 mb-6">
                    {plan.features.map((feature, featureIndex) => (
                      <li key={featureIndex} className="flex items-start text-sm">
                        <i className="ri-check-line text-green-500 mr-2 mt-0.5 flex-shrink-0"></i>
                        <span className="text-gray-770">{feature}</span>
                      </li>
                    ))}
                  </ul>

                  <Button
                    onClick={plan.buttonAction}
                    variant="outline"
                    className="w-full"
                  >
                    Comenzar
                  </Button>
                </div>
              </div>
            )
          ))}
        </div>
      </div>
    </section>
  );
}